import React, {useState} from "react";
import {connect} from "react-redux";

import {
    Fab,
    Tooltip,
    createTheme,
} from "@mui/material"

import { ThemeProvider } from '@mui/material/styles';
import { withStyles} from "@mui/styles";
import showStyle from "assets/jss/views/picking/order/showStyle";
import {BeatLoader} from "react-spinners";
import {blackMagicColor, dangerColor, primaryColor, successColor} from "assets/jss/main";
import {ReactComponent as Check} from 'assets/img/check.svg';
import {ReactComponent as Close} from 'assets/img/close.svg';
import {FormattedMessage, injectIntl} from "react-intl";

import {Authorizations, hasAuthorization} from "utils/authorizations";
import {LineItemTransitions} from "utils/lineItemTransitions";
import { checkIfAllAssigned } from "utils/LineItemStatus";
import { confirm, reserve } from "actions/order/update";
import ModalWaybillSelector from 'views/picking/components/ModalWaybillSelector';
import { isLineItemWmsHandled } from "utils/WMSActivation";

function OrderStatus({ classes, retrieved, authorizations, member, updateLoadingOrder, updateLoadingLineItem, confirm, reserve, state, openCancellationDialogByTransitionAndItem, refreshOrder, toggleModalWaybillSelector, waitingTimeBeforeRefreshingList, isWmsEnabled, currentOrganization, ...props }) {
    const lineItems = retrieved["hydra:member"];

    const theme = createTheme({
        palette: {
            primary: {main: successColor},
            secondary: {main: dangerColor},
            default: {main: primaryColor},
            blackMagic: {main: blackMagicColor},
        }
    });

    function handleClickConfirm(orderId) {
        if (window.confirm(props.intl.formatMessage({id: "picking.order.show.table.window.confirmAll"})))
            confirm(orderId);
    };

    function handleClickReserve(orderId) {
        if (window.confirm(props.intl.formatMessage({id: "picking.order.show.table.window.reserveAll"})))
            reserve(orderId);
    };

    function isWmsOrder() {
        return isWmsEnabled && lineItems.every((item) => null !== item.wmsItem)
    }

    function renderOrderStatus() {
        let isRetailer = hasAuthorization(authorizations, Authorizations.PICKING_RETAILER);
        let orderStatus = lineItems[0].order.status;

        const statusIsPending = lineItems[0].order.status === "pending";
        const statusIsReserved = lineItems[0].order.status === "reserved";
        const statusIsConfirmed = lineItems[0].order.status === "confirmed";
        const statusIsToShip = lineItems[0].order.status === "to-ship";

        const canConfirm = hasAuthorization(authorizations, Authorizations.PICKING_RETAILER_CONFIRM);
        const canCancel = hasAuthorization(authorizations, Authorizations.PICKING_RETAILER_CANCEL);
        const canRefund = hasAuthorization(authorizations, Authorizations.PICKING_RETAILER_REFUND);
        const canReserve = hasAuthorization(authorizations, Authorizations.PICKING_RETAILER_RESERVE);
        const canCancelReserved = hasAuthorization(authorizations, Authorizations.PICKING_RETAILER_CANCEL_RESERVED);
        const isReadOnly = hasAuthorization(authorizations, Authorizations.PICKING_READ_ONLY);

        const oneHandledByWMS = isWmsEnabled && lineItems.some(lineItem => isLineItemWmsHandled(lineItem, currentOrganization));
        const onHandledBySAP = lineItems.some(lineItem => lineItem['customMetaData'] && lineItem['customMetaData']['isSAP'] === true);

        // Custom case for SAP handled items
        var customWorkflowCanConfirm = true;
        var customWorkflowCanCancel = true;
        var customWorkflowCanShip = true;
        var customWorkflowCanReserve = true;
        if (onHandledBySAP || oneHandledByWMS) {
            customWorkflowCanConfirm = false;
            customWorkflowCanCancel = statusIsToShip;
            customWorkflowCanShip = false;
        }
        if (onHandledBySAP) {
            customWorkflowCanReserve = false;
        }


        const order = lineItems[0].order;

        let isAssigned = checkIfAllAssigned(lineItems, member);

        let primaryFunction = null;
        let primaryTooltipMessageId = null;
        let secondaryFunction = null;
        let secondaryTooltipMessageId = null;

        if (statusIsPending && customWorkflowCanReserve) {
            if (isRetailer || isAssigned || canReserve) {
                primaryFunction = handleClickReserve;
                primaryTooltipMessageId = "picking.order.show.table.action.reserve";
            }
            if ((!isRetailer && (isAssigned )) || canCancel || canCancelReserved) {
                secondaryFunction = () => {
                    openCancellationDialogByTransitionAndItem(order, [
                        LineItemTransitions.CANCEL,
                        LineItemTransitions.UNAVAILABLE
                    ])
                }
                secondaryTooltipMessageId = "picking.order.show.table.action.cancel";
            }
        } else if (statusIsConfirmed) {
            if ((!isRetailer) || canRefund) {
                secondaryFunction = () => {
                    openCancellationDialogByTransitionAndItem(order, [LineItemTransitions.CANCEL])
                }
                secondaryTooltipMessageId = "picking.order.show.table.action.refund";
            }
        } else if (statusIsReserved && customWorkflowCanConfirm) {
            if (isAssigned || (isRetailer && canConfirm)) {
                primaryFunction = handleClickConfirm;
                primaryTooltipMessageId = "picking.order.show.table.action.confirm";
            }
            if ((!isRetailer && (isAssigned )) || canCancel || canCancelReserved) {
                secondaryFunction = () => {
                    openCancellationDialogByTransitionAndItem(order, [
                        LineItemTransitions.CANCEL,
                        LineItemTransitions.UNAVAILABLE
                    ])
                }
                secondaryTooltipMessageId = "picking.order.show.table.action.cancel";
            }
        } else if (statusIsToShip && customWorkflowCanCancel) {
            if ((!isRetailer && (isAssigned )) || canCancel) {
                secondaryFunction = () => {
                    openCancellationDialogByTransitionAndItem(order, [LineItemTransitions.CANCEL])
                }
                secondaryTooltipMessageId = "picking.order.show.table.action.cancel";
            }
        }

        if (isReadOnly) {
            primaryFunction = null;
            primaryTooltipMessageId = null;
            secondaryFunction = null;
            secondaryTooltipMessageId = null;
        }

        let orderStatusClass;
        let orderShippingMethod;

        if ('pending' !== orderStatus && 'reserved' !== orderStatus) {
            lineItems.map(lineItem => {
                if ('to-ship' === lineItem.status) {
                    orderStatus = 'to-ship'
                }
                orderShippingMethod = lineItem.shippingMethod.type;
            });
        }

        switch (orderStatus) {
            case 'to-ship':
                orderStatusClass = classes.toShip;

                break;
            case 'confirmed':
                orderStatusClass = classes.confirmed;

                break;
            case 'canceled':
                orderStatusClass = classes.canceled;

                break;

            case 'reserved':
                orderStatusClass = classes.reserved;

                break;

            case 'returning':
                orderStatusClass = classes.returning;

                break;
            default:
                orderStatusClass = classes[orderStatus];
        }

        return (
            <div className={classes.actionContainer}>
                <div className={classes.orderStatus}>
                    <ThemeProvider theme={theme}>
                        {updateLoadingOrder || updateLoadingLineItem || waitingTimeBeforeRefreshingList ?
                            <BeatLoader
                                sizeUnit={"px"}
                                size={4}
                                color={primaryColor}
                                loading={true}
                            /> :
                            <span className={classes.orderActions}>
                                {primaryFunction !== null &&
                                    <Fab
                                        color="primary"
                                        size={'small'}
                                        className={classes.validationButton}
                                        onClick={() => primaryFunction(lineItems[0].order["@id"])}
                                    >
                                        <Tooltip title={<FormattedMessage id={primaryTooltipMessageId}/>}>
                                            <Check />
                                        </Tooltip>
                                    </Fab>
                                }
                                {secondaryFunction !== null && !isWmsOrder() &&
                                    <Fab
                                        color="secondary"
                                        size={'small'}
                                        className={classes.cancelButton}
                                        onClick={() => secondaryFunction(lineItems[0].order["@id"])}
                                    >
                                        <Tooltip title={<FormattedMessage id={secondaryTooltipMessageId}/>}>
                                            <Close />
                                        </Tooltip>
                                    </Fab>
                                }
                            </span>
                        }
                        <ModalWaybillSelector
                            open={state.modalOpen}
                            onClose={toggleModalWaybillSelector}
                            refreshOrder={(waitingTime) => refreshOrder(waitingTime)}
                            lineItems={lineItems}
                            lineItemSelectedIds={state.lineItemSelectedIds}
                            selectedShipment={state.selectedShipment}
                        />
                    </ThemeProvider>
                </div>
            </div>
        );
    };

    return (
        renderOrderStatus()
    );
}

const mapStateToProps = state => {
    return {
        retrieved: state.lineItem.list.retrieved,
        authorizations: state.authentication.authorizations,
        member: state.authentication.member,
        updateLoadingOrder: state.order.update.loading,
        updateLoadingLineItem: state.lineItem.update.loading,
    };
};

const mapDispatchToProps = dispatch => ({
    confirm: (orderId) => dispatch(confirm(orderId)),
    reserve: (orderId) => dispatch(reserve(orderId)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withStyles(showStyle)(injectIntl(OrderStatus)));
